/* PCIS — BlueDart Origin Area (route /supply/pcis-health/bluedart-origin-area).
 *
 * Ops-managed mapping of a pickup pincode → BlueDart origin/billing area. The BlueDart adapter reads
 * Shipper.OriginArea (and the pickup AreaCode) from this table by pickup pincode, falling back to the
 * account's configured origin_area when a pincode is absent. Ops own correctness/coverage.
 *
 * Admins search by pincode, add/edit/delete inline, or bulk-load via Excel (same shape as BlueDart's
 * source sheet: CAREA | CSCRCD | CPINCODE | STATE | REGION | BILLING AREA). All calls go to ProShip
 * /api/pcis-admin/bluedart-origin-area (which proxies PCIS); the browser never sees PCIS's admin key.
 * PCIS caches the lookup — after edits it auto-evicts, and the cache is also clearable under PCIS → Cache.
 */
import React, { useState } from 'react'
import { Box, Stack, Button, Typography, IconButton, Tooltip, Chip, Alert, TextField } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, Modal, FileDrop, ConfirmDialog, TableSkeleton, LoadErrorPanel, useToast } from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { get } from '../../../common/constants'
import { downloadExcelFile } from '../../../common/util'
import {
  usePcisBlueDartOriginAreasQuery,
  useUpsertPcisBlueDartOriginAreaMutation,
  useDeletePcisBlueDartOriginAreaMutation,
  useUploadPcisBlueDartOriginAreaMutation,
} from '../../../redux/supplyApiSlice'

const EMPTY_FORM = {
  id: null, pincode: '', billing_area: '', region: '', state: '', area: '', service_center_code: '',
}

export default function PcisBlueDartOriginArea() {
  const toast = useToast()

  const [pincodeInput, setPincodeInput] = useState('')
  const [appliedPincode, setAppliedPincode] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(50)

  const [form, setForm] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadResult, setUploadResult] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const {
    data, isFetching, isError, refetch,
  } = usePcisBlueDartOriginAreasQuery({ pincode: appliedPincode, page, size: sizePerPage })

  const rows = data?.content || []
  const rowCount = data?.totalElements || 0

  const [upsert, { isLoading: saving }] = useUpsertPcisBlueDartOriginAreaMutation()
  const [remove, { isLoading: deleting }] = useDeletePcisBlueDartOriginAreaMutation()
  const [upload, { isLoading: uploading }] = useUploadPcisBlueDartOriginAreaMutation()

  const columns = [
    { field: 'pincode', headerName: 'Pincode', width: 120 },
    {
      field: 'billingArea', headerName: 'Origin / Billing Area', width: 170,
      renderCell: (p) => <Chip size='small' color='primary' variant='outlined' label={p.row.billingArea} />,
    },
    { field: 'region', headerName: 'Region', width: 110, renderCell: (p) => p.row.region || <span style={{ opacity: 0.5 }}>—</span> },
    { field: 'state', headerName: 'State', flex: 1, minWidth: 140, renderCell: (p) => p.row.state || <span style={{ opacity: 0.5 }}>—</span> },
    { field: 'area', headerName: 'Area (CAREA)', width: 130, renderCell: (p) => p.row.area || <span style={{ opacity: 0.5 }}>—</span> },
    { field: 'serviceCenterCode', headerName: 'SC Code', width: 110, renderCell: (p) => p.row.serviceCenterCode || <span style={{ opacity: 0.5 }}>—</span> },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction='row' spacing={0.5}>
          <Tooltip title='Edit'>
            <IconButton size='small' onClick={() => openEdit(p.row)}><EditOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
          <Tooltip title='Delete'>
            <IconButton size='small' color='error' onClick={() => setDeleteTarget(p.row)}><DeleteOutlineOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  function applySearch() {
    setPage(0)
    setAppliedPincode(pincodeInput.trim())
  }

  function openAdd() { setForm({ ...EMPTY_FORM }) }
  function openEdit(row) {
    setForm({
      id: row.id,
      pincode: row.pincode,
      billing_area: row.billingArea || '',
      region: row.region || '',
      state: row.state || '',
      area: row.area || '',
      service_center_code: row.serviceCenterCode || '',
    })
  }

  async function saveForm() {
    if (!form.pincode || !String(form.pincode).trim()) return toast('Pincode is required.', 'error')
    if (!/^\d{6}$/.test(String(form.pincode).trim())) return toast('Pincode must be 6 digits.', 'error')
    if (!form.billing_area || !String(form.billing_area).trim()) return toast('Origin / billing area is required.', 'error')
    const body = {
      pincode: String(form.pincode).trim(),
      billing_area: String(form.billing_area).trim().toUpperCase(),
      region: form.region || null,
      state: form.state || null,
      area: form.area || null,
      service_center_code: form.service_center_code || null,
    }
    try {
      await upsert(body).unwrap()
      toast(`Saved pincode ${body.pincode}.`, 'success')
      setForm(null)
      refetch()
    } catch (e) {
      toast(e?.message || 'Could not save.', 'error')
    }
  }

  async function confirmDelete() {
    try {
      await remove({ id: deleteTarget.id }).unwrap()
      toast('Deleted.', 'success')
      setDeleteTarget(null)
      refetch()
    } catch (e) {
      toast(e?.message || 'Could not delete.', 'error')
      setDeleteTarget(null)
    }
  }

  async function downloadTemplate() {
    setDownloading(true)
    try {
      const res = await get('pcis-admin/bluedart-origin-area/template', {}, { responseType: 'blob' })
      downloadExcelFile(res.data, 'bluedart_origin_area_template')
    } catch (e) {
      toast('Could not download the template.', 'error')
    }
    setDownloading(false)
  }

  async function doUpload() {
    if (!uploadFile) return toast('Choose an .xlsx file first.', 'error')
    try {
      const result = await upload({ file: uploadFile }).unwrap()
      setUploadResult(result)
      const failed = (result && result.validation_errors && result.validation_errors.length) || 0
      toast(`Uploaded: ${result?.sent_to_pcis ?? 0} row(s) sent${failed ? `, ${failed} rejected` : ''}.`,
        failed ? 'warning' : 'success')
      refetch()
    } catch (e) {
      toast(e?.message || 'Upload failed.', 'error')
    }
  }

  function closeUpload() {
    setUploadOpen(false)
    setUploadFile(null)
    setUploadResult(null)
  }

  return (
    <>
      <PageTitle title='PCIS · BlueDart Origin Area' />
      <PageHeader
        title='BlueDart Origin Area'
        sub='Pickup pincode → BlueDart origin/billing area. Used to set Shipper.OriginArea; unmapped pincodes fall back to the account default. Edit inline or bulk-upload the sheet.'
        actions={
          <>
            <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />}
              onClick={downloadTemplate} disabled={downloading}>Template</Button>
            <Button size='small' variant='outlined' startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setUploadOpen(true)}>Upload Excel</Button>
            <Button size='small' variant='contained' startIcon={<AddOutlinedIcon />}
              onClick={openAdd}>Add Pincode</Button>
          </>
        }
      />

      <Stack direction='row' spacing={1} sx={{ mb: 2, maxWidth: 460 }}>
        <TextField
          size='small' fullWidth
          label='Search pincode'
          value={pincodeInput}
          onChange={(e) => setPincodeInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applySearch() }}
          placeholder='Full or partial pincode, then Enter'
        />
        <Button size='small' variant='outlined' startIcon={<SearchOutlinedIcon />} onClick={applySearch}>Search</Button>
        {appliedPincode && (
          <Button size='small' onClick={() => { setPincodeInput(''); setAppliedPincode(''); setPage(0) }}>Clear</Button>
        )}
      </Stack>

      {isFetching ? (
        <TableSkeleton />
      ) : isError ? (
        <LoadErrorPanel onRetry={refetch} />
      ) : (
        <Box sx={{ height: 640 }}>
          <ProzoDataGrid
            columns={columns}
            rows={rows}
            getRowId={(r) => r.id}
            rowCount={rowCount}
            paginationMode='server'
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            pagination
          />
        </Box>
      )}

      {/* ── Add / Edit ─────────────────────────────────────────────────────── */}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form && form.id ? 'Edit Origin Area' : 'Add Origin Area'}
        width={520}
        actions={[
          { label: 'Cancel', onClick: () => setForm(null) },
          { label: saving ? 'Saving…' : 'Save', primary: true, onClick: saveForm, disabled: saving, closeOnClick: false },
        ]}
      >
        {form && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Pincode (6 digits)' size='small' fullWidth
              value={form.pincode}
              onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
              disabled={!!form.id}   // pincode is the natural key — locked on edit
              helperText='Pickup pincode this origin area applies to'
            />
            <TextField
              label='Origin / Billing Area' size='small' fullWidth
              value={form.billing_area}
              onChange={(e) => setForm((f) => ({ ...f, billing_area: e.target.value }))}
              helperText='Sent as Shipper.OriginArea, e.g. GGN / BOM / BLR'
            />
            <TextField label='Region (optional)' size='small' fullWidth
              value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} />
            <TextField label='State (optional)' size='small' fullWidth
              value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
            <Stack direction='row' spacing={2}>
              <TextField label='Area / CAREA (optional)' size='small' fullWidth
                value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} />
              <TextField label='SC Code / CSCRCD (optional)' size='small' fullWidth
                value={form.service_center_code} onChange={(e) => setForm((f) => ({ ...f, service_center_code: e.target.value }))} />
            </Stack>
          </Stack>
        )}
      </Modal>

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title='Delete origin-area row?'
        body={deleteTarget ? `${deleteTarget.pincode} → ${deleteTarget.billingArea}` : ''}
        okLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onOk={confirmDelete}
      />

      {/* ── Upload Excel ───────────────────────────────────────────────────── */}
      <Modal
        open={uploadOpen}
        onClose={closeUpload}
        title='Upload BlueDart Origin Areas'
        width={640}
        actions={[
          { label: 'Close', onClick: closeUpload },
          { label: uploading ? 'Uploading…' : 'Upload', primary: true, onClick: doUpload, disabled: uploading || !uploadFile, closeOnClick: false },
        ]}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2' sx={{ opacity: 0.8 }}>
            Columns: <b>CAREA | CSCRCD | CPINCODE | STATE | REGION | BILLING AREA</b> (BlueDart's sheet
            format — upload it directly). Existing pincodes are updated, new ones inserted; the lookup
            cache refreshes automatically.
          </Typography>
          <FileDrop
            accept='.xlsx'
            title='Drop the .xlsx here or click to choose'
            hint='CPINCODE and BILLING AREA are required per row'
            onFile={(f) => { setUploadFile(f); setUploadResult(null) }}
            onError={(m) => toast(m, 'error')}
          />
          {uploadFile && <Typography variant='caption'>Selected: {uploadFile.name}</Typography>}

          {uploadResult && (
            <Box>
              <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
                <Chip size='small' color='success' label={`Sent: ${uploadResult.sent_to_pcis ?? 0}`} />
                {(uploadResult.validation_errors || []).length > 0 &&
                  <Chip size='small' color='error' label={`Rejected: ${uploadResult.validation_errors.length}`} />}
              </Stack>
              {(uploadResult.validation_errors || []).length > 0 && (
                <Alert severity='warning' sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {uploadResult.validation_errors.slice(0, 200).map((er, i) => (
                    <div key={i}>Row {er.row}: {er.pincode || '—'} — {er.error}</div>
                  ))}
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </Modal>
    </>
  )
}
